import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['V Bottom', 'Pram Bow', 'River Boat', 'Landing Craft', 'Specmar', 'Custom', 'In Action'];

const GalleryAdmin = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingImage, setEditingImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('All');

  const emptyImage = { src: '', title: '', category: 'V Bottom', order: 0, active: true };

  useEffect(() => { fetchImages(); }, []);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data);
    } catch (err) {
      console.error('Failed to fetch images', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingImage.id) {
        await axios.put(`${API}/admin/gallery/${editingImage.id}`, editingImage, { headers });
      } else {
        await axios.post(`${API}/admin/gallery`, editingImage, { headers });
      }
      
      setShowModal(false);
      setEditingImage(null);
      fetchImages();
    } catch (err) {
      alert('Failed to save image: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchImages();
    } catch (err) {
      alert('Failed to delete image');
    }
  };

  const filteredImages = filter === 'All' ? images : images.filter(img => img.category === filter);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="gallery-admin-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Gallery</h1>
            <p className="text-slate-500 mt-1">Manage gallery images</p>
          </div>
          <button
            onClick={() => { setEditingImage({ ...emptyImage, order: images.length }); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-image-btn"
          >
            <Plus className="w-5 h-5" />
            Add Image
          </button>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['All', ...categories].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 text-sm font-medium ${
                filter === cat ? 'bg-orange-500 text-white' : 'bg-white border-2 border-slate-100 text-slate-600 hover:border-orange-500'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Images Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredImages.map((img) => (
            <div key={img.id} className="bg-white border-2 border-slate-100 overflow-hidden group" data-testid={`gallery-item-${img.id}`}>
              <div className="aspect-square relative">
                <img src={img.src} alt={img.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => { setEditingImage(img); setShowModal(true); }}
                    className="p-2 bg-white text-slate-700 hover:bg-orange-500 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(img.id)}
                    className="p-2 bg-white text-slate-700 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {!img.active && (
                  <span className="absolute top-2 left-2 text-xs bg-red-500 text-white px-2 py-0.5">Inactive</span>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-slate-900 truncate">{img.title}</p>
                <p className="text-xs text-slate-500">{img.category}</p>
              </div>
            </div>
          ))}
        </div>

        {filteredImages.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <p className="text-slate-500">No images in this category. Add some!</p>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && editingImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white w-full max-w-md">
              <div className="border-b border-slate-100 p-6 flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase">
                  {editingImage.id ? 'Edit Image' : 'Add Image'}
                </h2>
                <button onClick={() => { setShowModal(false); setEditingImage(null); }} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {editingImage.src && (
                  <div className="aspect-video bg-slate-100 overflow-hidden">
                    <img src={editingImage.src} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}

                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Image URL *</label>
                  <input
                    type="text"
                    value={editingImage.src}
                    onChange={(e) => setEditingImage({ ...editingImage, src: e.target.value })}
                    className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Title *</label>
                  <input
                    type="text"
                    value={editingImage.title}
                    onChange={(e) => setEditingImage({ ...editingImage, title: e.target.value })}
                    className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="Image title"
                  />
                </div>

                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Category *</label>
                  <select
                    value={editingImage.category}
                    onChange={(e) => setEditingImage({ ...editingImage, category: e.target.value })}
                    className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingImage.active}
                    onChange={(e) => setEditingImage({ ...editingImage, active: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label className="text-slate-700">Active (visible on website)</label>
                </div>
              </div>

              <div className="border-t border-slate-100 p-6 flex justify-end gap-4">
                <button onClick={() => { setShowModal(false); setEditingImage(null); }} className="px-6 py-3 text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Save Image
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default GalleryAdmin;
